/**
 * Karta opiekuna w panelu najemcy (sekcja Pomoc).
 *
 * Dwie rzeczy w jednym miejscu, bo w praktyce padają razem:
 *   1. KTO mnie obsługuje i jak się z nim skontaktować — dane partnera
 *      przypisanego do organizacji albo obsługującego jej województwo.
 *      Najemca nie ma (i nie powinien mieć) dostępu do tabeli `partners`, gdzie
 *      leżą prowizje i numer konta — dane kontaktowe wydaje RPC.
 *   2. ZGODA na wejście partnera na konto: sześciocyfrowy kod, który najemca
 *      podaje przez telefon.
 *
 * Cyfry kodu widać DOKŁADNIE RAZ — w bazie leży wyłącznie skrót. Po odświeżeniu
 * ekranu zostaje sam termin ważności i możliwość odwołania zgody. Pod spodem
 * najemca widzi każde wejście na swoje konto i pełny dziennik: co partner
 * otwierał i co zmieniał.
 */

import type { SupportAccessEvent, SupportAccessGrant } from '@insolitum/types';
import {
  Clock,
  Copy,
  Eye,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  UserCog,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { useKartaPomocy, useSesjeWsparcia } from '../../hooks/useSupportAccess';

interface Props {
  organizationId: string;
}

export function KartaOpiekuna({ organizationId }: Props) {
  const { karta, isLoading, error, wygenerujKod, odwolajKod, refetch } =
    useKartaPomocy(organizationId);
  const {
    sesje,
    refetch: odswiezSesje,
    pobierzDziennik,
    zakonczSesje,
  } = useSesjeWsparcia(organizationId, 20);

  const [kod, setKod] = useState<SupportAccessGrant | null>(null);
  const [minuty, setMinuty] = useState(30);
  const [powod, setPowod] = useState('');
  const [pracuje, setPracuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [dziennikSesji, setDziennikSesji] = useState<{
    id: string;
    wpisy: SupportAccessEvent[];
  } | null>(null);

  const wydaj = async () => {
    setPracuje(true);
    setBlad(null);
    try {
      const g = await wygenerujKod({ minutes: minuty, purpose: powod || undefined });
      setKod(g);
      await odswiezSesje();
    } catch (err) {
      setBlad(err instanceof Error ? err.message : 'Nie udało się wydać kodu');
    } finally {
      setPracuje(false);
    }
  };

  const odwolaj = async () => {
    if (!karta?.aktywny_grant_id) return;
    setPracuje(true);
    try {
      await odwolajKod(karta.aktywny_grant_id);
      setKod(null);
    } catch (err) {
      setBlad(err instanceof Error ? err.message : 'Nie udało się odwołać zgody');
    } finally {
      setPracuje(false);
    }
  };

  const otworzDziennik = async (sessionId: string) => {
    const wpisy = await pobierzDziennik(sessionId);
    setDziennikSesji({ id: sessionId, wpisy });
  };

  if (isLoading) {
    return (
      <div className="bg-gray-800/50 border border-gray-700 rounded-ids-lg p-6">
        <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Opiekun */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-ids-lg p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <UserCog className="w-4 h-4 text-amber-400" />
          Twój opiekun
        </h3>

        {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

        {/* Opiekun regionu — konkretna osoba z zespołu partnera odpowiedzialna
            za województwo tej organizacji (migracja 473). Stoi PRZED danymi
            firmy, bo to jest odpowiedź na pytanie „do kogo dzwonić"; kontakt
            firmowy zostaje niżej jako droga odwoławcza. */}
        {karta?.opiekun_user_id && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-ids-md p-4">
            <p className="text-xs text-amber-300/90 mb-2">
              Twój opiekun{karta.opiekun_obszar ? ` — ${karta.opiekun_obszar}` : ''}
            </p>
            <p className="text-aurora-text font-medium">
              {karta.opiekun_imie ?? 'Opiekun regionu'}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
              {karta.opiekun_email && (
                <a
                  href={`mailto:${karta.opiekun_email}`}
                  className="text-blue-300 hover:underline break-all inline-flex items-center gap-1"
                >
                  <Mail className="w-3 h-3" />
                  {karta.opiekun_email}
                </a>
              )}
              {karta.opiekun_telefon && (
                <a
                  href={`tel:${karta.opiekun_telefon}`}
                  className="text-blue-300 hover:underline inline-flex items-center gap-1"
                >
                  <Phone className="w-3 h-3" />
                  {karta.opiekun_telefon}
                </a>
              )}
            </div>
          </div>
        )}

        {karta?.partner_name ? (
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Firma partnerska</p>
              <p className="text-aurora-text">{karta.partner_name}</p>
              {karta.przypisanie === 'region' && (
                <p className="text-xs text-gray-500">opiekun regionu {karta.voivodeship ?? ''}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500">Osoba kontaktowa</p>
              <p className="text-aurora-text">{karta.contact_name ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Mail className="w-3 h-3" /> E-mail
              </p>
              {karta.email ? (
                <a
                  href={`mailto:${karta.email}`}
                  className="text-blue-300 hover:underline break-all"
                >
                  {karta.email}
                </a>
              ) : (
                <p className="text-aurora-text">—</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Telefon
              </p>
              {karta.phone ? (
                <a href={`tel:${karta.phone}`} className="text-blue-300 hover:underline">
                  {karta.phone}
                </a>
              ) : (
                <p className="text-aurora-text">—</p>
              )}
            </div>
            {(karta.city || karta.voivodeship) && (
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Siedziba
                </p>
                <p className="text-aurora-text">
                  {karta.city ?? ''}
                  {karta.voivodeship ? `, ${karta.voivodeship}` : ''}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            Do Twojej organizacji nie jest przypisany partner. Zgłoszenia obsługuje zespół INSOLITUM
            przez formularz poniżej.
          </p>
        )}
      </div>

      {/* Zgoda na wejście */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-ids-lg p-5">
        <h3 className="text-sm font-semibold text-gray-300 mb-1 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-emerald-400" />
          Wpuść opiekuna na swoje konto
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Wygeneruj kod i podaj go opiekunowi przez telefon. Zobaczy dokładnie to, co Ty. Kod jest
          jednorazowy, wygasa po wybranym czasie, a <strong>cała sesja jest nagrywana</strong> —
          poniżej zobaczysz, co robił.
        </p>

        {blad && <p className="text-sm text-red-400 mb-3">{blad}</p>}

        {kod ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-ids-md p-4 mb-4">
            <p className="text-xs text-emerald-300 mb-2">
              Podaj ten kod opiekunowi. Zobaczysz go tylko teraz.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-mono font-bold tracking-[0.3em] text-aurora-text">
                {kod.code}
              </span>
              <button
                onClick={() => navigator.clipboard?.writeText(kod.code)}
                className="p-2 text-emerald-300 hover:bg-emerald-500/20 rounded-ids-md"
                title="Kopiuj"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              ważny do {new Date(kod.expires_at).toLocaleTimeString('pl-PL')} · dla{' '}
              {kod.partner_name ?? 'przypisanego partnera'}
            </p>
          </div>
        ) : karta?.aktywny_grant_id ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-ids-md p-4 mb-4 flex items-center justify-between">
            <p className="text-sm text-amber-200">
              Kod czeka na użycie (ważny do{' '}
              {karta.grant_expires_at
                ? new Date(karta.grant_expires_at).toLocaleTimeString('pl-PL')
                : '—'}
              ). Cyfry były widoczne tylko przy wydaniu.
            </p>
            <button
              onClick={odwolaj}
              disabled={pracuje}
              className="px-3 py-1.5 text-xs bg-gray-700 text-aurora-text rounded-ids-md hover:bg-gray-600 disabled:opacity-50"
            >
              Odwołaj
            </button>
          </div>
        ) : null}

        {/*
          Wyboru zakresu tu nie ma i to jest decyzja, nie przeoczenie: „tylko
          podgląd" nie było pilnowane przez żadną politykę bazy (wejście zakłada
          zwykłe czasowe członkostwo, a polityki zapisu patrzą właśnie na
          członkostwo). Zgoda opisana słowami, których uprawnienia nie
          potwierdzają, jest gorsza niż brak wyboru — więc dopóki zakres nie
          jest egzekwowany, mówimy wprost, co się dzieje. Migracja 462.
        */}
        <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-ids-md px-3 py-2 mb-3">
          Partner, który użyje kodu, zobaczy i będzie mógł zmienić dokładnie to, co Ty. Każde
          wejście na ekran, każdy podgląd danych i każda zmiana są zapisywane w dzienniku poniżej.
        </p>

        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Kod ważny (minuty)</label>
            <input
              type="number"
              min={5}
              max={120}
              value={minuty}
              onChange={(e) => setMinuty(Number.parseInt(e.target.value, 10) || 30)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-ids-md text-aurora-text text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              W jakiej sprawie (opcjonalnie)
            </label>
            <input
              type="text"
              value={powod}
              onChange={(e) => setPowod(e.target.value)}
              placeholder="np. grafik nie chce się zapisać"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-ids-md text-aurora-text text-sm"
            />
          </div>
        </div>

        <button
          onClick={wydaj}
          disabled={pracuje || !karta?.partner_org_id}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-aurora-on-accent text-sm font-medium rounded-ids-md hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50"
        >
          {pracuje ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <KeyRound className="w-4 h-4" />
          )}
          Wygeneruj kod dostępu
        </button>
        {!karta?.partner_org_id && (
          <p className="text-xs text-gray-500 mt-2">
            Kod można wydać dopiero, gdy do organizacji przypisany jest partner.
          </p>
        )}
      </div>

      {/* Historia wejść */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-ids-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-400" />
            Wejścia na Twoje konto ({sesje.length})
          </h3>
          <button
            onClick={() => refetch()}
            className="text-xs text-gray-400 hover:text-aurora-text"
          >
            Odśwież
          </button>
        </div>

        {sesje.length === 0 ? (
          <p className="text-sm text-gray-500">Nikt nigdy nie wchodził na Twoje konto.</p>
        ) : (
          <ul className="divide-y divide-gray-700/50">
            {sesje.map((s) => {
              const trwa = !s.ended_at && Date.parse(s.expires_at) > Date.now();
              return (
                <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-aurora-text">
                      {s.rodzaj === 'serwis'
                        ? 'Insolitum — wsparcie techniczne'
                        : (s.partner_name ?? s.actor_name ?? s.actor_email ?? 'partner')}
                      {trwa && (
                        <span className="ml-2 px-2 py-0.5 text-[11px] rounded bg-emerald-500/20 text-emerald-300">
                          trwa teraz
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(s.started_at).toLocaleString('pl-PL')} · zakres {s.scope} ·{' '}
                      {s.events_count} zdarzeń, {s.changes_count} zmian
                      {s.ended_at ? ` · zakończona (${s.ended_reason})` : ''}
                    </p>
                    {s.rodzaj === 'serwis' && s.powod && (
                      <p className="text-xs text-gray-400">Powód: {s.powod}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => otworzDziennik(s.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-700 text-gray-200 rounded-ids-md hover:bg-gray-600"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Dziennik
                    </button>
                    {trwa && (
                      <button
                        onClick={() => zakonczSesje(s.id, 'najemca')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-red-500/20 text-red-300 rounded-ids-md hover:bg-red-500/30"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Zakończ
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Dziennik sesji */}
      {dziennikSesji && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setDziennikSesji(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-ids-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-5 py-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-aurora-text">
                Dziennik sesji ({dziennikSesji.wpisy.length} wpisów)
              </h4>
              <button
                onClick={() => setDziennikSesji(null)}
                className="p-1.5 text-gray-400 hover:text-aurora-text hover:bg-gray-800 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="p-5 space-y-2">
              {dziennikSesji.wpisy.map((w, i) => (
                <li
                  key={`${w.occurred_at}-${i}`}
                  className="text-xs border-l-2 border-gray-700 pl-3 py-1"
                >
                  <span className="text-gray-500">
                    {new Date(w.occurred_at).toLocaleTimeString('pl-PL')}
                  </span>{' '}
                  <span
                    className={
                      w.kind === 'zmiana'
                        ? 'text-amber-300'
                        : w.kind === 'wejscie' || w.kind === 'wyjscie'
                          ? 'text-blue-300'
                          : 'text-gray-300'
                    }
                  >
                    {w.kind}
                  </span>{' '}
                  <span className="text-aurora-text">{w.summary ?? w.action ?? ''}</span>
                  {w.route && <span className="text-gray-500"> · {w.route}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default KartaOpiekuna;
